import { useState, useRef, useMemo, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import HTMLFlipBook from "react-pageflip";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

/* ================= PDF WORKER ================= */
pdfjs.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

/* ================= CONFIG ================= */
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

const Booklet = ({ catalogueUrl }) => {
  const [pages, setPages] = useState(null);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [size, setSize] = useState({ width: 400, height: 600 });

  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const flipRef = useRef(null);
  const lastDistance = useRef(null);

  const isMobile = useMemo(() => window.innerWidth < 768, []);
  const isTablet = useMemo(
  () => window.innerWidth >= 768 && window.innerWidth <= 1024,
  []
);
const flipbookStyle = useMemo(() => {
  if (isMobile) {
    return {
      width: size.width,
      position: "relative",
      left: "15%",
      top: "-7%",
      transform: `translateX(-50%) scale(${zoom})`,
      transformOrigin: "center center",
    };
  }

  if (isTablet) {
    return {
      width: size.width,
      position: "relative",
      left: "10%",       // 👈 tablet specific
      top: "-18%",         // 👈 tablet specific
      transform: `translateX(-50%) scale(${zoom})`,
      transformOrigin: "center center",
    };
  }

  // 🖥 DESKTOP
  return {
    width: size.width,
    position: "relative",
    left: "0",
    top: "0",
    transform: `scale(${zoom})`,
    transformOrigin: "center center",
  };
}, [isMobile, isTablet, size.width, zoom]);


const RENDER_SCALE = useMemo(() => {
  if (isMobile) return 1.4;
  if (isTablet) return 1.6;
  return 1; // ✅ desktop MUST be 1 for HTMLFlipBook
}, [isMobile, isTablet]);



  /* ================= PDF SOURCE ================= */
  const proxyUrl = `https://api.gulf.deals/store-offer/pdf?url=${catalogueUrl}`;
  const pdfFile = useMemo(() => ({ url: proxyUrl }), [proxyUrl]);

  /* ================= FIT FULL SCREEN ================= */
  useEffect(() => {
    const updateSize = () => {
      if (!containerRef.current) return;

      const { clientWidth, clientHeight } = containerRef.current;
      const ratio = 1.42;

      if (window.innerWidth < 768) {
        // 📱 MOBILE
        const width = clientWidth * 0.5;
        const height = width * ratio;
        setSize({ width, height });
      } 
       else if (window.innerWidth <= 1024) {
  // 📱 TABLET (iPad)
  const width = clientWidth * 0.5;
  const height = width * ratio;
  setSize({ width, height });

}
      
      else {
        // 🖥 DESKTOP
        let height = clientHeight * 0.98;
        let width = height / ratio;

        if (width > clientWidth * 0.98) {
          width = clientWidth * 0.98;
          height = width * ratio;
        }

        setSize({ width, height });
      }
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  /* ================= PREVENT BROWSER ZOOM ================= */
  useEffect(() => {
    const preventZoom = e => {
      if (e.ctrlKey) e.preventDefault();
    };
    window.addEventListener("wheel", preventZoom, { passive: false });
    return () => window.removeEventListener("wheel", preventZoom);
  }, []);

  /* ================= CTRL + WHEEL ================= */
  const handleWheelZoom = e => {
    if (!e.ctrlKey) return;
    e.preventDefault();

    setZoom(z =>
      Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z - e.deltaY * 0.002))
    );
  };

  /* ================= PINCH ================= */
  const handlePinch = e => {
    if (e.touches.length !== 2) return;

    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    const dist = Math.hypot(dx, dy);

    if (!lastDistance.current) {
      lastDistance.current = dist;
      return;
    }

    const delta = dist - lastDistance.current;
    setZoom(z =>
      Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z + delta * 0.002))
    );

    lastDistance.current = dist;
  };

  /* ================= ZOOM BUTTONS ================= */
  const zoomIn = () => setZoom(z => Math.min(MAX_ZOOM, z + 0.25));
  const zoomOut = () => setZoom(z => Math.max(MIN_ZOOM, z - 0.25));

  /* ================= AUTO CENTER WHEN ZOOM = 1 ================= */
  useEffect(() => {
    if (!scrollRef.current || zoom !== 1) return;

    scrollRef.current.scrollTo({
      left:
        (scrollRef.current.scrollWidth -
          scrollRef.current.clientWidth) / 2,
      top:
        (scrollRef.current.scrollHeight -
          scrollRef.current.clientHeight) / 2,
      behavior: "smooth",
    });
  }, [zoom]);

  /* ================= RENDER ================= */
  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden bg-black/5"
    >
      {/* PAGE COUNTER */}
      <div className="absolute top-4 left-4 z-20 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
        {page} / {pages || "-"}
      </div>

      {/* NAV */}
      <button
        onClick={() => flipRef.current?.pageFlip().flipPrev()}
        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-3 bg-black/60 text-white rounded-full"
      >
        <ChevronLeft />
      </button>

      <button
        onClick={() => flipRef.current?.pageFlip().flipNext()}
        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-3 bg-black/60 text-white rounded-full"
      >
        <ChevronRight />
      </button>

      {/* ZOOM */}
      <div className="absolute bottom-4 sm:bottom-6 right-4 sm:right-6 z-20 flex bg-black/60 rounded-full p-1">
        <button onClick={zoomOut} className="p-2 text-white">
          <ZoomOut />
        </button>
        <button onClick={zoomIn} className="p-2 text-white">
          <ZoomIn />
        </button>
      </div>

      {/* VIEWPORT */}
      <div
        ref={scrollRef}
        className="absolute inset-0 overflow-auto"
        onWheel={handleWheelZoom}
        onTouchMove={handlePinch}
      >
        <div
          className="flex items-center justify-center w-full h-full"
          style={{
            padding: zoom > 1 ? 40 : 0,
          }}
        >
<div
  style={flipbookStyle}
>

            <Document
              file={pdfFile}
              onLoadSuccess={({ numPages }) => setPages(numPages)}
            >
              {pages && (
                <HTMLFlipBook
                  ref={flipRef}
                  width={size.width}
                  height={size.height}
                  size="fixed"
                  showCover = {true}
                  // usePortrait={isMobile} 
                  mobileScrollSupport
                  useMouseEvents={!isMobile && zoom === 1}
                  disableFlipByClick={zoom !== 1}
                  onFlip={e => setPage(e.data + 1)}
                >
                  {Array.from({ length: pages }, (_, i) => (
                    <div key={i} className="bg-white">
                      <Page
                        pageNumber={i + 1}
                        width={size.width}
                        height={size.height}
                        scale={RENDER_SCALE}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </div>
                  ))}
                </HTMLFlipBook>
              )}
            </Document>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booklet;
